import endpoints from '@/config/endpoints'
import { Locale } from '@/types'

// Gets all query params
export const getQueryParams = () => {
  const params = new URLSearchParams(window.location.search)

  const result: { [key: string]: string } = {}
  for (const [key, value] of params.entries()) {
    result[key] = value
  }
  return result
}

/**
 * Creates a debounced function that delays invoking the provided function
 * until after `wait` milliseconds have elapsed since the last time it was invoked.
 *
 * @param func - The function to debounce.
 * @param wait - The number of milliseconds to delay.
 * @return A new debounced function.
 */
export function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeoutId: number | undefined

  return function (...args: Parameters<T>) {
    clearTimeout(timeoutId)
    timeoutId = window.setTimeout(() => {
      func(...args)
    }, wait)
  }
}

// Function to construct image path dynamically
export const getImagePath = (imagePath: string | undefined) => {
  if (!imagePath) return ''
  const pathParts = imagePath.split('/')

  const productId = pathParts[2]
  const uniqueId = pathParts[3]
  const filename = pathParts[4]

  return `${endpoints.images}product/${productId}/${uniqueId}/L/${filename}`
}

// Extracts unique values from an array of objects
export const extractUniqueValues = <T extends object, K extends keyof T>(
  items: Array<T>,
  key: K
): Array<T[K]> => {
  const ids = items.flatMap(item => item[key] || [])
  return [...new Set(ids)]
}

type CalendarDate = { year: number; month: number; day: number }

/** YYYY-MM-DD from a date string or ISO timestamp, without a timezone shift. */
function parseCalendarDate(value: string): CalendarDate | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim())
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  if (month < 1 || month > 12 || day < 1 || day > 31) return null
  return { year, month, day }
}

function calendarAge(birthday: CalendarDate, reference: CalendarDate): number {
  let age = reference.year - birthday.year
  const birthdayNotYet =
    reference.month < birthday.month ||
    (reference.month === birthday.month && reference.day < birthday.day)
  if (birthdayNotYet) age--
  return age
}

/**
 * Age label for a traveller. `referenceDate` is the stay departure when the
 * résumé should show the age used for the quote. Without it, age is as of today.
 * Anyone 18 or older is shown as `18+`.
 */
export const getAge = (birthday: string, referenceDate?: string): string => {
  const birth = parseCalendarDate(birthday || '')
  if (!birth) return '1'

  const today = new Date()
  const reference = referenceDate
    ? parseCalendarDate(referenceDate)
    : {
        year: today.getFullYear(),
        month: today.getMonth() + 1,
        day: today.getDate(),
      }
  if (!reference) return '1'

  let age = calendarAge(birth, reference)
  if (age <= 1) age = 1
  return age >= 18 ? '18+' : `${age}`
}

export const validateName = (value?: string | null): boolean => {
  return value !== undefined && value !== null && value.trim().length !== 0
}

export const extractErrorMessage = (
  error: unknown,
  fallback: string
): string => {
  let message = ''
  if (error instanceof Error) {
    message = error.message
  } else {
    message = fallback
  }

  return message
}

/**
 * Normalizes the given locale by converting 'en-GB' to 'en-US'.
 * This ensures consistency in locale formatting where only 'en-US' is expected.
 *
 * @param locale - The locale string to normalize.
 * @returns The normalized locale string.
 */
export function normalizeLocale(locale: Locale): Locale {
  return locale === 'en-GB' ? 'en-US' : locale;
}

// Export location codes utilities
export { getAirportName, formatAirportDisplay, AIRPORT_CODES } from './locationCodes'